import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect, memo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Plane, NotebookText, CalendarDays } from '@/components/icons'
import { Avatar, AvatarFallback } from './ui/avatar'
import { useRosterGantt, useCrewGetAll, useAircraftMaintenance } from '../lib/hooks'
import { MaintenanceIndicator } from './MaintenanceIndicator'
import type { MaintenanceAircraft } from '../lib/hooks/useAircraftMaintenance'
import { DutyStatus } from '../../../api/src/shared'
import { crewRoleBadge, crewRoleLabels, type CrewRoleBadge } from '@@api/shared'
import { cn, getDutyStatusColor, getInitials } from '@/lib/utils'
import dayjs, { Dayjs } from 'dayjs'
import type { RosterFilters } from './RosterFilterDrawer'
import { EmptyState } from './EmptyState'
import { Skeleton } from './ui/skeleton'

/** Info about a selected roster entry (single day) */
export interface SelectedRosterEntry {
  entryId: string
  crewId: string
  date: Date
  dutyStatus: DutyStatus
  crew?: {
    id: string
    name: string
    firstName?: string | null
    lastName?: string | null
    avatarColor?: string | null
  }
}

/** Info about which entry to focus/highlight after create/update */
export interface FocusedEntryInfo {
  crewId: string
  startDate: Date
  endDate: Date
}

/** Info for creating a new roster entry on an empty cell */
export interface EmptyCellInfo {
  crewId: string
  date: Date
  crew: {
    id: string
    name: string
    firstName?: string | null
    lastName?: string | null
    avatarColor?: string | null
  }
}

interface RosterGanttViewProps {
  onEntryClick?: (entry: SelectedRosterEntry) => void
  onEmptyCellClick?: (info: EmptyCellInfo) => void
  onCrewAvatarClick?: (crewId: string) => void
  /** Entry to scroll to and highlight after create/update */
  focusedEntry?: FocusedEntryInfo | null
  /** Callback when focus animation completes */
  onFocusComplete?: () => void
  /** Filters to apply to roster data */
  filters?: RosterFilters
  /** Additional CSS classes for the root container */
  className?: string
}

function getPriorityForStatus(status: DutyStatus): number {
  switch (status) {
    case DutyStatus.OnDuty:
      return 6
    case DutyStatus.Training:
      return 5
    case DutyStatus.Positioning:
      return 4
    case DutyStatus.Standby:
      return 3
    case DutyStatus.Leave:
    case DutyStatus.Sick:
      return 2
    case DutyStatus.OffDuty:
      return 1
    default:
      return 0
  }
}

// Role badge on the avatar (Danielle: pilots vs FAs weren't distinguishable
// without tapping each name). Tokens only; pilots darkest so they read first.
const ROLE_BADGE_CLASS: Record<CrewRoleBadge, string> = {
  P: 'bg-content-primary-bold',
  FA: 'bg-content-primary-subtle',
  E: 'bg-content-primary-subtle-mid',
}

// Cell dimensions
const GAP_WIDTH = 4
const AVATAR_WIDTH = 76
const VISIBLE_DAYS = 7
const MIN_CELL_WIDTH = 24
const MAX_CELL_WIDTH = 36
// Placeholder rows while the roster loads
const SKELETON_ROWS = 8
// The timeline always spans at least this many months either side of today,
// so there's room to scroll into (and add entries on) empty months.
const MIN_MONTHS_AROUND_TODAY = 12

// Column windowing. The scroller keeps the full timeline's width, but only the
// days around the viewport are rendered (crew × days runs into the tens of
// thousands of cells otherwise). Days rendered beyond the visible ones on each
// side:
const WINDOW_PAD_DAYS = 45
// How far the left-edge day may drift from the window anchor before the window
// re-centres. Leaves ≥ WINDOW_PAD_DAYS - REANCHOR_DAYS buffered days on the
// near side at all times.
const REANCHOR_DAYS = 24

const DATE_KEY = 'YYYY-MM-DD'

/** Whole days from `from` to `to` (both local midnights; DST-safe). */
function daysBetween(from: Dayjs, to: Dayjs): number {
  return Math.round(to.startOf('day').diff(from.startOf('day'), 'day', true))
}

/**
 * The crew roster as one continuous timeline (crew rows × days).
 *
 * Every roster entry and flight day is fetched once (`roster.getGantt`) and the
 * aircraft / crew / duty status filters run here on the client, so neither
 * scrolling nor filtering ever waits on the network. Only the days around the
 * viewport are rendered.
 *
 * The URL `selectedMonth` param stays the shared month for the date nav, KPI
 * card and the other views: scrolling writes the month under the left edge
 * back to it, and a month picked in the date nav scrolls the grid there.
 */
export function RosterGanttView({
  onEntryClick,
  onEmptyCellClick,
  onCrewAvatarClick,
  focusedEntry,
  onFocusComplete,
  filters,
  className,
}: RosterGanttViewProps) {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { selectedMonth?: string }
  const rootRef = useRef<HTMLDivElement>(null)
  const hScrollRef = useRef<HTMLDivElement>(null)
  // Header is a separate horizontal scroller kept in sync with the body so the
  // day row stays pinned while the crew rows scroll vertically.
  const headerScrollRef = useRef<HTMLDivElement>(null)

  // Selected month from the URL, normalised to 'YYYY-MM'.
  const parsedMonth = search.selectedMonth ? dayjs(search.selectedMonth, 'YYYY-MM') : null
  const monthKey = (parsedMonth?.isValid() ? parsedMonth : dayjs()).format('YYYY-MM')

  // Size cells so exactly VISIBLE_DAYS fit the container width.
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const update = () => setContainerWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const CELL_WIDTH = useMemo(() => {
    if (containerWidth <= 0) return MAX_CELL_WIDTH
    const available = containerWidth - AVATAR_WIDTH - GAP_WIDTH * (VISIBLE_DAYS - 1)
    return Math.max(MIN_CELL_WIDTH, Math.min(MAX_CELL_WIDTH, available / VISIBLE_DAYS))
  }, [containerWidth])
  const STEP = CELL_WIDTH + GAP_WIDTH

  // All roster entries and flight days, loaded once.
  const { data: gantt, isPending: pendingRoster } = useRosterGantt()
  const rosterEntries = gantt?.entries
  const flights = gantt?.flights

  // Crew members. Crew excluded from the schedule never appear on the roster.
  const {
    data: rawCrewMembers = [],
    isPending: pendingCrew,
  } = useCrewGetAll()
  const crewMembers = useMemo(
    () => rawCrewMembers.filter((c) => c.includeInSchedule !== false),
    [rawCrewMembers]
  )

  // Maintenance lookup: selected aircraft, or all aircraft when none selected
  const { getMaintenanceForDay, hasMaintenance } = useAircraftMaintenance(filters?.aircraftIds)

  const loading = pendingRoster || pendingCrew

  // crewId -> date -> tripId
  const crewFlightMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {}
    flights?.forEach((flight) => {
      const dateKey = dayjs(flight.departureDateTime).format(DATE_KEY)
      map[flight.crewId] ??= {}
      map[flight.crewId][dateKey] = flight.tripId
    })
    return map
  }, [flights])

  // crewId -> date -> entry (highest-priority status wins on a shared day),
  // scoped to the selected aircraft when one is picked.
  const aircraftFilterKey = filters?.aircraftIds?.join(',') ?? ''
  const crewStatusMap = useMemo(() => {
    const aircraftIds = aircraftFilterKey ? new Set(aircraftFilterKey.split(',')) : null
    const map: Record<string, CrewStatusByDate> = {}
    rosterEntries?.forEach((entry) => {
      if (aircraftIds && !aircraftIds.has(entry.aircraftId)) return
      map[entry.crewId] ??= {}
      // Stored as a plain YYYY-MM-DD string
      const dateKey = entry.date.slice(0, 10)
      const existing = map[entry.crewId][dateKey]
      if (
        !existing ||
        getPriorityForStatus(entry.dutyStatus as DutyStatus) >
          getPriorityForStatus(existing.status)
      ) {
        map[entry.crewId][dateKey] = {
          status: entry.dutyStatus as DutyStatus,
          entryId: entry.id,
          hasNote: entry.hasNote,
        }
      }
    })
    return map
  }, [rosterEntries, aircraftFilterKey])

  // Duty status filter applied on top
  const filteredCrewStatusMap = useMemo(() => {
    if (!filters?.dutyStatuses?.length) return crewStatusMap
    const filtered: typeof crewStatusMap = {}
    Object.entries(crewStatusMap).forEach(([crewId, dates]) => {
      const kept: CrewStatusByDate = {}
      Object.entries(dates).forEach(([dateKey, data]) => {
        if (filters.dutyStatuses.includes(data.status)) kept[dateKey] = data
      })
      if (Object.keys(kept).length > 0) filtered[crewId] = kept
    })
    return filtered
  }, [crewStatusMap, filters?.dutyStatuses])

  // Rows: crew with at least one (filtered) entry in the month in view, by
  // name. The month follows the scroll, so rows change at month boundaries.
  const crewRows = useMemo(
    () =>
      crewMembers
        .filter((crew) => {
          if (filters?.crewMemberIds?.length && !filters.crewMemberIds.includes(crew.id)) {
            return false
          }
          // Date keys are YYYY-MM-DD, monthKey is YYYY-MM
          return Object.keys(filteredCrewStatusMap[crew.id] ?? {}).some((dateKey) =>
            dateKey.startsWith(monthKey)
          )
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [crewMembers, filters?.crewMemberIds, filteredCrewStatusMap, monthKey]
  )

  const hasActiveFilters =
    (filters?.crewMemberIds?.length ?? 0) > 0 ||
    (filters?.dutyStatuses?.length ?? 0) > 0 ||
    (filters?.aircraftIds?.length ?? 0) > 0

  // Timeline range: whole months covering all data, at least
  // MIN_MONTHS_AROUND_TODAY either side of today, and the selected month (a
  // month picked outside the data still gets columns). It only ever grows, so a
  // column never disappears from under the user.
  const rangeRef = useRef<{ start: string; end: string } | null>(null)
  const range = useMemo(() => {
    const today = dayjs()
    const selected = dayjs(monthKey, 'YYYY-MM')
    let start = today.subtract(MIN_MONTHS_AROUND_TODAY, 'month').startOf('month').format(DATE_KEY)
    let end = today.add(MIN_MONTHS_AROUND_TODAY, 'month').endOf('month').format(DATE_KEY)
    const include = (key: string) => {
      if (key < start) start = key
      if (key > end) end = key
    }
    include(selected.startOf('month').format(DATE_KEY))
    include(selected.endOf('month').format(DATE_KEY))
    rosterEntries?.forEach((entry) => include(entry.date.slice(0, 10)))
    flights?.forEach((flight) => include(dayjs(flight.departureDateTime).format(DATE_KEY)))
    if (rangeRef.current) {
      include(rangeRef.current.start)
      include(rangeRef.current.end)
    }
    // Snap to whole months so the timeline starts on a 1st
    start = dayjs(start).startOf('month').format(DATE_KEY)
    end = dayjs(end).endOf('month').format(DATE_KEY)
    const prev = rangeRef.current
    const next = prev && prev.start === start && prev.end === end ? prev : { start, end }
    rangeRef.current = next
    return next
  }, [monthKey, rosterEntries, flights])

  const allDays = useMemo(() => {
    const first = dayjs(range.start)
    const count = daysBetween(first, dayjs(range.end)) + 1
    return Array.from({ length: count }, (_, i) => first.add(i, 'day'))
  }, [range])
  const rangeStart = allDays[0]
  const totalWidth = allDays.length * STEP - GAP_WIDTH

  const dayIndexOf = useCallback(
    (day: Dayjs) => Math.min(allDays.length - 1, Math.max(0, daysBetween(rangeStart, day))),
    [allDays.length, rangeStart]
  )
  /** Scroll target for a month: today on the current month, the 1st otherwise. */
  const monthTargetIndex = useCallback(
    (month: string) =>
      dayIndexOf(month === dayjs().format('YYYY-MM') ? dayjs() : dayjs(month, 'YYYY-MM')),
    [dayIndexOf]
  )

  // Rendered window of days around the left-edge anchor. Starts at the initial
  // scroll target so the first paint already shows the right cells.
  const [anchorIndex, setAnchorIndex] = useState(() => monthTargetIndex(monthKey))
  const windowStart = Math.max(0, anchorIndex - WINDOW_PAD_DAYS)
  const windowEnd = Math.min(allDays.length, anchorIndex + VISIBLE_DAYS + WINDOW_PAD_DAYS)
  const windowDays = useMemo(
    () => allDays.slice(windowStart, windowEnd),
    [allDays, windowStart, windowEnd]
  )
  const leadingOffset = windowStart * STEP

  // Month currently under the left edge, as last synced with the URL.
  const syncedMonthRef = useRef<string | null>(null)
  // Months the grid has written to the URL that the URL hasn't settled on yet.
  // A fast fling crosses several months before the router catches up, so the
  // URL can briefly show an older write; the URL -> scroll effect must treat
  // those as its own (skip), not as a month picked in the date nav (jump).
  const pendingWritesRef = useRef<string[]>([])
  const searchRef = useRef(search)
  searchRef.current = search
  const allDaysRef = useRef(allDays)
  allDaysRef.current = allDays

  // On every scroll: keep the pinned header aligned, re-centre the rendered
  // window once the left edge drifts REANCHOR_DAYS from the anchor (functional
  // update, so usually a no-op), and write the month under the left edge to
  // the URL when it changes.
  const handleScroll = useCallback(() => {
    const el = hScrollRef.current
    if (!el) return
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft
    const days = allDaysRef.current
    const idx = Math.min(days.length - 1, Math.max(0, Math.round(el.scrollLeft / STEP)))
    setAnchorIndex((prev) => (Math.abs(idx - prev) > REANCHOR_DAYS ? idx : prev))
    const month = days[idx]?.format('YYYY-MM')
    if (month && month !== syncedMonthRef.current) {
      syncedMonthRef.current = month
      pendingWritesRef.current.push(month)
      navigate({
        search: { ...searchRef.current, selectedMonth: month } as any,
        replace: true,
      })
    }
  }, [STEP, navigate])

  const scrollToDayIndex = useCallback(
    (idx: number) => {
      if (!hScrollRef.current) return
      hScrollRef.current.scrollLeft = Math.max(0, idx * STEP)
      // Runs inside layout effects: re-renders the window before paint, so a
      // jump never shows a frame of blank cells.
      handleScroll()
    },
    [STEP, handleScroll]
  )

  // Columns added at the start (data that reaches further back arrived) shift
  // every day index; move the scroll position and anchor with them so the view
  // stays on the same dates.
  const prevRangeStartRef = useRef(range.start)
  useLayoutEffect(() => {
    const prev = prevRangeStartRef.current
    prevRangeStartRef.current = range.start
    if (prev === range.start) return
    const added = daysBetween(rangeStart, dayjs(prev))
    setAnchorIndex((a) => a + added)
    if (hScrollRef.current && syncedMonthRef.current !== null) {
      hScrollRef.current.scrollLeft += added * STEP
    }
  }, [range.start, rangeStart, STEP])

  // URL -> scroll: jump to the selected month on first load and whenever the
  // date nav (arrows, month picker, deep link) picks a month the grid didn't
  // scroll to itself.
  useLayoutEffect(() => {
    if (loading) return
    const pending = pendingWritesRef.current
    if (pending.includes(monthKey)) {
      // Our own write; once the URL reaches the latest one, it has settled.
      if (monthKey === pending[pending.length - 1]) pendingWritesRef.current = []
      return
    }
    if (monthKey === syncedMonthRef.current) return
    pendingWritesRef.current = []
    syncedMonthRef.current = monthKey
    scrollToDayIndex(monthTargetIndex(monthKey))
  }, [monthKey, loading, monthTargetIndex, scrollToDayIndex])

  // Cell width changes (resize, first measure) move every column; keep the
  // same day at the left edge.
  const prevStepRef = useRef(STEP)
  useLayoutEffect(() => {
    const prev = prevStepRef.current
    prevStepRef.current = STEP
    const el = hScrollRef.current
    if (prev === STEP || !el) return
    el.scrollLeft = Math.round(el.scrollLeft / prev) * STEP
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft
  }, [STEP])

  // Cell currently highlighted after create/update.
  const [focusedCell, setFocusedCell] = useState<{ crewId: string; dateKey: string } | null>(
    null
  )

  // Row callbacks are handed to memoized rows, so they must be referentially
  // stable; the parent passes inline arrows, so route through refs.
  const onEntryClickRef = useRef(onEntryClick)
  onEntryClickRef.current = onEntryClick
  const onEmptyCellClickRef = useRef(onEmptyCellClick)
  onEmptyCellClickRef.current = onEmptyCellClick
  const onFocusCompleteRef = useRef(onFocusComplete)
  onFocusCompleteRef.current = onFocusComplete
  const handleEntryClick = useCallback(
    (entry: SelectedRosterEntry) => onEntryClickRef.current?.(entry),
    []
  )
  const handleEmptyCellClick = useCallback(
    (info: EmptyCellInfo) => onEmptyCellClickRef.current?.(info),
    []
  )

  // Focus a newly created/updated entry: scroll its first day to the left edge
  // (which also moves the URL month) and pulse the cell.
  useLayoutEffect(() => {
    if (!focusedEntry || loading) return
    const start = dayjs(focusedEntry.startDate)
    scrollToDayIndex(dayIndexOf(start))
    setFocusedCell({ crewId: focusedEntry.crewId, dateKey: start.format(DATE_KEY) })
    const timer = setTimeout(() => {
      setFocusedCell(null)
      onFocusCompleteRef.current?.()
    }, 2000)
    return () => clearTimeout(timer)
  }, [focusedEntry, loading, dayIndexOf, scrollToDayIndex])

  const showEmpty = !loading && crewRows.length === 0

  return (
    <div ref={rootRef} className={cn('flex flex-col w-full overflow-hidden', className)}>
      {/* Two-axis scroll with a pinned day header. The header is a separate
           horizontal scroller above the body; its scrollLeft is synced to the
           body in handleScroll so it never scrolls vertically (avoids fragile
           nested position:sticky on iOS).
           data-sentry-block / ph-no-capture keep the grid out of both session
           recorders, which otherwise serialize every cell on the main thread. */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Pinned day header — mirrors the body's horizontal scroll */}
        <div
          ref={headerScrollRef}
          data-sentry-block
          className="ph-no-capture shrink-0 overflow-x-hidden overflow-y-hidden [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', touchAction: 'none' }}
        >
          <div className="flex" style={{ width: `${totalWidth + AVATAR_WIDTH + 8}px` }}>
            <div
              className="sticky left-0 z-50 bg-surface-brand-L0 shrink-0"
              style={{ width: `${AVATAR_WIDTH}px` }}
            />
            <div style={{ width: `${totalWidth}px` }}>
              <GanttDayHeader
                days={windowDays}
                leadingOffset={leadingOffset}
                cellWidth={CELL_WIDTH}
                hasMaintenance={hasMaintenance}
                getMaintenanceForDay={getMaintenanceForDay}
              />
            </div>
          </div>
        </div>

        {/* Body: vertical scroller wrapping the horizontal scroller. It stays
            mounted with no rows (the empty state overlays it) so the scroll
            position and month sync survive a filter that matches nothing. */}
        <div
          data-sentry-block
          className="ph-no-capture relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden"
          style={{
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}
        >
          <div
            ref={hScrollRef}
            onScroll={handleScroll}
            className="[&::-webkit-scrollbar]:hidden"
            style={{
              overflowX: 'auto',
              overflowY: 'clip',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
              // X only: `contain` on the y axis makes Chrome/Android swallow
              // vertical swipes instead of chaining them to the outer scroller.
              overscrollBehaviorX: 'contain',
              // Both axes: overflow-y clip stops this element scrolling
              // vertically, so vertical gestures bubble to the outer scroller.
              // pan-x alone traps vertical gestures on iOS.
              touchAction: 'pan-x pan-y',
              // Fill the body even with zero rows so an empty result still
              // accepts horizontal scrolling.
              minHeight: '100%',
            }}
          >
            <div className="flex" style={{ width: `${totalWidth + AVATAR_WIDTH + 8}px` }}>
              {/* Sticky avatar column */}
              <div
                className="sticky left-0 z-50 bg-surface-brand-L0 shrink-0 overflow-visible"
                style={{ width: `${AVATAR_WIDTH}px` }}
              >
                {loading
                  ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1 shrink-0 pr-2"
                        style={{ height: `${CELL_WIDTH + 8}px`, width: `${AVATAR_WIDTH}px` }}
                      >
                        <Skeleton className="size-6 rounded-full shrink-0" />
                        <Skeleton className="h-2.5 w-8" />
                      </div>
                    ))
                  : crewRows.map((crew) => {
                      const roleBadge = crewRoleBadge(crew.crewRole)
                      const roleLabel = crew.crewRole
                        ? crewRoleLabels[crew.crewRole as keyof typeof crewRoleLabels]
                        : null
                      return (
                        <button
                          key={crew.id}
                          onClick={() => onCrewAvatarClick?.(crew.id)}
                          className="flex items-center gap-1 shrink-0 hover:cursor-pointer pr-2"
                          style={{ height: `${CELL_WIDTH + 8}px`, width: `${AVATAR_WIDTH}px` }}
                        >
                          {/* Wrapper carries the badge: Avatar is overflow-hidden and would clip it */}
                          <span className="relative shrink-0">
                            <Avatar className="size-6 shrink-0">
                              <AvatarFallback
                                className="text-background text-[10px]"
                                style={
                                  crew.avatarColor
                                    ? { backgroundColor: crew.avatarColor }
                                    : undefined
                                }
                              >
                                {getInitials(crew.firstName, crew.lastName)}
                              </AvatarFallback>
                            </Avatar>
                            {roleBadge && (
                              <span
                                aria-hidden
                                className={cn(
                                  'absolute -bottom-0.5 -right-1 flex h-3 min-w-3 items-center justify-center rounded-full px-0.5',
                                  'text-[7px] font-bold leading-none tracking-[0px] text-content-primary-inverse ring-1 ring-surface-L0',
                                  ROLE_BADGE_CLASS[roleBadge]
                                )}
                              >
                                {roleBadge}
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] font-semibold text-content-primary-bold tracking-[0px] truncate">
                            {crew.firstName || crew.name}
                          </span>
                          <span className="sr-only">
                            {crew.name}
                            {roleLabel ? `, ${roleLabel}` : ''}
                          </span>
                        </button>
                      )
                    })}
              </div>

              {/* Grid area */}
              <div className="flex flex-col gap-2" style={{ width: `${totalWidth}px` }}>
                {loading
                  ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                      <GanttSkeletonRow
                        key={i}
                        days={windowDays}
                        leadingOffset={leadingOffset}
                        cellWidth={CELL_WIDTH}
                      />
                    ))
                  : crewRows.map((crew) => (
                      <GanttCrewRow
                        key={crew.id}
                        crew={crew}
                        days={windowDays}
                        leadingOffset={leadingOffset}
                        cellWidth={CELL_WIDTH}
                        statusMap={filteredCrewStatusMap[crew.id]}
                        flightMap={crewFlightMap[crew.id]}
                        focusedDateKey={
                          focusedCell?.crewId === crew.id ? focusedCell.dateKey : null
                        }
                        onEntryClick={handleEntryClick}
                        onEmptyCellClick={handleEmptyCellClick}
                      />
                    ))}
              </div>
            </div>
          </div>
          {showEmpty && (
            <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
              <EmptyState
                icon={CalendarDays}
                title="No roster entries"
                description={
                  hasActiveFilters
                    ? 'No roster entries match the current filters'
                    : 'No roster entries for this month'
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* Memoized pieces. Rows and the header bail out unless their own inputs
   changed, so a parent re-render (drawer open, focus pulse) is cheap. */

interface GanttDayHeaderProps {
  /** The rendered window of days (see WINDOW_PAD_DAYS) */
  days: Dayjs[]
  /** Width of the unrendered days before the window */
  leadingOffset: number
  cellWidth: number
  hasMaintenance: boolean
  getMaintenanceForDay: (day: Dayjs) => MaintenanceAircraft[]
}

const GanttDayHeader = memo(function GanttDayHeader({
  days,
  leadingOffset,
  cellWidth,
  hasMaintenance,
  getMaintenanceForDay,
}: GanttDayHeaderProps) {
  const today = dayjs()
  return (
    <div
      className={cn(
        'flex items-end bg-surface-brand-L0 pb-2',
        // The 1st carries an extra "MMM" label; with the wrench slot that
        // column overflows 80px, so give it room whenever the slot is reserved.
        hasMaintenance ? 'h-24' : 'h-20'
      )}
      style={{ gap: `${GAP_WIDTH}px`, paddingLeft: `${leadingOffset}px` }}
    >
      {days.map((day) => {
        const isFirstOfMonth = day.date() === 1
        const isToday = day.isSame(today, 'day')
        const isPast = day.isBefore(today, 'day')
        return (
          <div
            key={day.format('YYYY-MM-DD')}
            className={cn(
              'flex flex-col items-center justify-between gap-2 shrink-0',
              isPast && 'opacity-50'
            )}
            style={{ width: `${cellWidth}px` }}
          >
            {isFirstOfMonth && (
              <p className="text-[10px] text-content-primary-bold font-medium tracking-[0.18px] -mb-0.5">
                {day.format('MMM')}
              </p>
            )}
            <p className="text-xs text-content-primary-subtle tracking-[0.18px]">
              {day.format('ddd')}
            </p>
            {isToday ? (
              <div className="flex items-center justify-center size-6 rounded-full bg-background-brand-bold">
                <p className="text-sm font-medium text-content-primary-inverse tracking-[0.07px]">
                  {day.format('D')}
                </p>
              </div>
            ) : (
              <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                {day.format('D')}
              </p>
            )}
            {/* Reserve a uniform slot on every column when the selected
                aircraft has maintenance, so day numbers stay aligned
                whether or not a wrench shows. */}
            {hasMaintenance && (
              <div className="flex items-center justify-center h-3">
                <MaintenanceIndicator
                  aircraft={getMaintenanceForDay(day)}
                  iconClassName="size-3"
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

const GanttSkeletonRow = memo(function GanttSkeletonRow({
  days,
  leadingOffset,
  cellWidth,
}: {
  days: Dayjs[]
  leadingOffset: number
  cellWidth: number
}) {
  const today = dayjs()
  return (
    <div
      className="flex"
      style={{ gap: `${GAP_WIDTH}px`, height: `${cellWidth}px`, paddingLeft: `${leadingOffset}px` }}
    >
      {days.map((day) => (
        <div
          key={day.format('YYYY-MM-DD')}
          className={cn(
            'shrink-0 rounded-[10px] h-full border border-dashed border-border-L3',
            day.isBefore(today, 'day') && 'opacity-50'
          )}
          style={{ width: `${cellWidth}px` }}
        />
      ))}
    </div>
  )
})

type CrewStatusByDate = Record<
  string,
  { status: DutyStatus; entryId: string; hasNote: boolean }
>

interface GanttCrewRowProps {
  crew: {
    id: string
    name: string
    firstName?: string | null
    lastName?: string | null
    avatarColor?: string | null
  }
  /** The rendered window of days (see WINDOW_PAD_DAYS) */
  days: Dayjs[]
  /** Width of the unrendered days before the window */
  leadingOffset: number
  cellWidth: number
  /** This crew's date -> entry map (after duty status filtering) */
  statusMap: CrewStatusByDate | undefined
  /** This crew's date -> tripId map */
  flightMap: Record<string, string> | undefined
  /** Date key of the cell to pulse, only for the focused crew's row */
  focusedDateKey: string | null
  onEntryClick: (entry: SelectedRosterEntry) => void
  onEmptyCellClick: (info: EmptyCellInfo) => void
}

const GanttCrewRow = memo(function GanttCrewRow({
  crew,
  days,
  leadingOffset,
  cellWidth,
  statusMap,
  flightMap,
  focusedDateKey,
  onEntryClick,
  onEmptyCellClick,
}: GanttCrewRowProps) {
  const today = dayjs()
  const crewInfo = {
    id: crew.id,
    name: crew.name,
    firstName: crew.firstName,
    lastName: crew.lastName,
    avatarColor: crew.avatarColor,
  }

  return (
    <div
      className="flex relative"
      style={{ gap: `${GAP_WIDTH}px`, height: `${cellWidth}px`, paddingLeft: `${leadingOffset}px` }}
    >
      {days.map((day) => {
        const dateKey = day.format('YYYY-MM-DD')
        const entryData = statusMap?.[dateKey]
        const isPast = day.isBefore(today, 'day')
        const hasFlight = !!flightMap?.[dateKey]

        // Empty cell - tap to create an entry. A flight on a day with no
        // entry still shows its plane.
        if (!entryData) {
          return (
            <button
              key={dateKey}
              onClick={() =>
                onEmptyCellClick({ crewId: crew.id, date: day.toDate(), crew: crewInfo })
              }
              className={cn(
                'flex items-center justify-center shrink-0 rounded-[10px] h-full border border-dashed border-border-L3 hover:bg-surface-L2 transition-colors',
                isPast && 'opacity-50'
              )}
              style={{ width: `${cellWidth}px` }}
            >
              {hasFlight && <Plane className="size-5 shrink-0 text-content-primary-inverse" />}
            </button>
          )
        }

        const showNoteIcon = entryData.hasNote
        // Shrink icons when both share the cell so they fit side by side
        const iconSize = hasFlight && showNoteIcon ? 'size-4' : 'size-5'

        // Filled cell - tap to edit the entry
        return (
          <button
            key={dateKey}
            onClick={() =>
              onEntryClick({
                entryId: entryData.entryId,
                crewId: crew.id,
                date: day.toDate(),
                dutyStatus: entryData.status,
                crew: crewInfo,
              })
            }
            className={cn(
              'flex items-center justify-center gap-0.5 shrink-0 rounded-[10px] h-full transition-all duration-300',
              getDutyStatusColor(entryData.status),
              {
                'opacity-50': isPast,
                'animate-pulse': focusedDateKey === dateKey,
              }
            )}
            style={{ width: `${cellWidth}px` }}
          >
            {hasFlight && (
              <Plane className={cn(iconSize, 'shrink-0 text-content-primary-inverse')} />
            )}
            {showNoteIcon && (
              <NotebookText className={cn(iconSize, 'shrink-0 text-content-primary-inverse')} />
            )}
          </button>
        )
      })}
    </div>
  )
})
